import React, { useCallback, useContext, useState } from "react";
import { ReCaptcha } from "../ReCaptcha";
import siteConfig from "../../config/siteConfig";
import { PageContext } from "../../context/PageContext";
import useUrl from "../../hooks/useUrl";
import { LinkInfo } from "../../models/shared/LinkInfo";
import Button, { ButtonSize } from "../Buttons/Button";
import { CheckboxList } from "../CheckboxList";
import Input from "../Input";
import { PolicyCheckbox } from "../PolicyCheckbox";
import useTranslations from "../../hooks/useTranslations";
import { localLanguageNames } from "../../context/LocalizationContext";

interface Props {
    policyLink: LinkInfo;
}

export const PressReleaseSignupForm = (props: Props) => {
    const formAction = "https://publish.ne.cision.com/Subscription/SubscribeWithCaptcha";
    const policyLinkUrl = useUrl(props.policyLink);

    const pageContext = useContext(PageContext);
    const lang = pageContext.i18nLang;
    
    const translations = useTranslations();

    const cisionLang = lang?.toLowerCase() === "sv-se"
        ? "sv"
        : "en";

    const [submitting, setSubmitting] = useState(false);
    const [submitSuccessful, setSubmitSuccessful] = useState<boolean | null>(null);
    const [submitFeedback, setSubmitFeedback] = useState<string | null>(null);

    const onSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();

        setSubmitting(true);

        const req = new XMLHttpRequest();

        req.onreadystatechange = () => {
            if (req.readyState !== 4) {
                return;
            }

            setSubmitSuccessful(req.status === 200);
            setSubmitting(false);

            if (req.status !== 200) {
                console.error(req.statusText, req.responseText);
                setSubmitFeedback(translations.error ?? "");
            }
            else {
                setSubmitFeedback(translations.message ?? "");
            }
        };

        const formData = new FormData(e.currentTarget as HTMLFormElement);

        // Policy checkbox is custom, no need to submit to Cision
        formData.delete("policy");

        req.open("POST", formAction);
        req.send(formData);
    }, [translations.error, translations.message]);

    let formCss = "transition duration-100";

    if (submitting) {
        formCss += " opacity-50";
    }

    if (submitSuccessful) {
        formCss += " opacity-0";
    }

    return (
        <>
            <div className="relative">
                <form
                    method="POST"
                    action={formAction}
                    onSubmit={onSubmit}
                    className={formCss}
                >
                    <input type="hidden" name="subscriptionUniqueIdentifier" value="2c9851bb5d" />
                    <input type="hidden" name="replylanguage" value={cisionLang} />

                    <Input
                        id="email"
                        label={translations.email ?? ""}
                        type="email"
                        required
                    />

                    <CheckboxList
                        name="language"
                        label={translations.selectLanguage ?? ""}
                        options={[
                            {
                                label: localLanguageNames["en"] ?? "English",
                                value: "en",
                                defaultChecked: cisionLang === "en"
                            },
                            {
                                label: localLanguageNames["sv-se"] ?? "Svenska",
                                value: "sv",
                                defaultChecked: cisionLang === "sv"
                            }
                        ]}
                    />

                    <CheckboxList
                        name="informationtype"
                        label={translations.informationType ?? ""}
                        options={[
                            { label: translations.reports ?? "", value: "kmk,rpt,rdv" },
                            { label: translations.pressReleases ?? "", value: "prm", defaultChecked: true }
                        ]}
                    />

                    {!!policyLinkUrl?.length &&
                        <PolicyCheckbox
                            id="policy"
                            value="true"
                            policyUrl={policyLinkUrl}
                            label={translations.policyCheckboxLabel ?? ""}
                            className="my-12"
                            required
                        />
                    }

                    <ReCaptcha
                        sitekey={siteConfig.cisionReCaptchaSiteKey}
                        render="explicit"
                        onloadCallback={() => {
                            // onloadCallback must be defined for explicit rendering to work
                        }}
                    />

                    <Button
                        type="submit"
                        size={ButtonSize.Normal}
                        color="primary-light"
                        className="mt-8"
                        disabled={(submitting || submitSuccessful) ?? false}
                    >
                        {submitting ? translations.submitting : translations.submit}
                    </Button>
                </form>

                {submitSuccessful &&
                    <div className="absolute top-0 left-0 w-full h-full flex items-center">
                        <p className="text-xl text-brand-primary-40">{submitFeedback}</p>
                    </div>
                }
            </div>

            {submitSuccessful === false &&
                <p className="text-red-600 my-6">{submitFeedback}</p>
            }
        </>
    );
};